import React, {useMemo, useState, useCallback} from "react";
import {ContactFooter, glass, filled, capsule} from "../ui";
import styled, {keyframes} from "styled-components";
import {useNavigate} from "react-router-dom";
import {familyTreeData} from "../familyTreeData";

const EVENT_TYPE = {
    BIRTHDAY: "birthday",
    MEMORIAL: "memorial",
    ANNIVERSARY: "anniversary",
};

const MONTH_NAMES_HE = [
    "ינואר", "פברואר", "מרץ", "אפריל", "מאי", "יוני",
    "יולי", "אוגוסט", "ספטמבר", "אוקטובר", "נובמבר", "דצמבר"
];

function parseMMDD(str) {
    if (!str) return null;
    const [m, d] = str.split("-").map(Number);
    return (m && d) ? {month: m, day: d} : null;
}

function formatDateHe(day, month) {
    return `${day} ב${MONTH_NAMES_HE[month - 1]}`;
}

const AVATAR_COLORS = ["#0984e3", "#6c5ce7", "#00b894", "#e17055", "#e84393", "#00cec9"];

function getInitials(firstName, lastName) {
    const first = (firstName || "").trim().charAt(0);
    const last = (lastName || "").trim().charAt(0);
    return (first + last) || "?";
}

function getAvatarColor(name) {
    let hash = 0;
    for (const ch of name || "") hash = (hash * 31 + ch.charCodeAt(0)) % 9973;
    return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

export function getAllEvents() {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const events = [];
    const seenAnniversaries = new Set();

    for (const member of familyTreeData) {
        const {data} = member;
        const name = `${data.firstName} ${data.lastName}`;
        const firstName = data.firstName;
        const lastName = data.lastName;
        const avatar = data.avatar;

        const isDeceased = !!(data.deathYear || data.memorialDate);
        const bd = parseMMDD(data.birthDate);
        if (bd && !isDeceased) {
            const birthYear = parseInt(data.birthday, 10) || null;
            let next = new Date(today.getFullYear(), bd.month - 1, bd.day);
            if (next < today) next = new Date(today.getFullYear() + 1, bd.month - 1, bd.day);
            const age = birthYear ? next.getFullYear() - birthYear : null;
            const diff = Math.round((next - today) / 86400000);
            events.push({name, firstName, lastName, avatar, memberId: member.id, type: EVENT_TYPE.BIRTHDAY, date: next, month: bd.month, day: bd.day, age, diffDays: diff});
        }

        const md = parseMMDD(data.memorialDate);
        if (md) {
            const deathYear = data.deathYear ? parseInt(data.deathYear, 10) : null;
            let next = new Date(today.getFullYear(), md.month - 1, md.day);
            if (next < today) next = new Date(today.getFullYear() + 1, md.month - 1, md.day);
            const yearsSince = deathYear ? next.getFullYear() - deathYear : null;
            const diff = Math.round((next - today) / 86400000);
            events.push({name, firstName, lastName, avatar, memberId: member.id, type: EVENT_TYPE.MEMORIAL, date: next, month: md.month, day: md.day, age: yearsSince, diffDays: diff});
        }

        const ad = parseMMDD(data.anniversaryDate);
        if (ad && data.spouseName) {
            const pairKey = [firstName, data.spouseName].sort().join("+");
            if (!seenAnniversaries.has(pairKey)) {
                seenAnniversaries.add(pairKey);
                const annYear = parseInt(data.anniversaryYear, 10) || null;
                let next = new Date(today.getFullYear(), ad.month - 1, ad.day);
                if (next < today) next = new Date(today.getFullYear() + 1, ad.month - 1, ad.day);
                const years = annYear ? next.getFullYear() - annYear : null;
                const diff = Math.round((next - today) / 86400000);
                events.push({
                    name: `${firstName} ו${data.spouseName}`,
                    firstName: `${firstName} ו${data.spouseName}`,
                    lastName,
                    avatar, memberId: member.id, type: EVENT_TYPE.ANNIVERSARY,
                    date: next, month: ad.month, day: ad.day, age: years, diffDays: diff
                });
            }
        }
    }

    events.sort((a, b) => a.diffDays - b.diffDays);
    return events;
}

function buildGoogleCalendarUrl(event) {
    const {type, age, firstName} = event;
    let title, details;

    if (type === EVENT_TYPE.BIRTHDAY) {
        title = age ? `🎂 יום הולדת ${age} ל${firstName}` : `🎂 יום הולדת ל${firstName}`;
        details = `יום הולדת ל${firstName}` + (age ? ` - גיל ${age}` : "");
    } else if (type === EVENT_TYPE.ANNIVERSARY) {
        title = age ? `💍 ${age} שנות נישואין - ${firstName}` : `💍 יום הנישואין - ${firstName}`;
        details = `יום הנישואין - ${firstName}` + (age ? ` - ${age} שנים` : "");
    } else {
        title = `🕯️ יום הזכרון ל${firstName}`;
        details = `יום הזכרון ל${firstName}` + (age ? ` - ${age} שנים` : "");
    }

    const dateStr = event.date.toISOString().slice(0, 10).replace(/-/g, "");
    const nextDay = new Date(event.date);
    nextDay.setDate(nextDay.getDate() + 1);
    const endStr = nextDay.toISOString().slice(0, 10).replace(/-/g, "");

    const params = new URLSearchParams({
        action: "TEMPLATE",
        text: title,
        dates: `${dateStr}/${endStr}`,
        details,
        recur: "RRULE:FREQ=YEARLY",
    });
    return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

function formatRelativeDay(diffDays) {
    if (diffDays === 0) return "היום!";
    if (diffDays === 1) return "מחר";
    if (diffDays <= 7) return `בעוד ${diffDays} ימים`;
    if (diffDays <= 30) {
        const weeks = Math.floor(diffDays / 7);
        return weeks === 1 ? "בעוד שבוע" : `בעוד ${weeks} שבועות`;
    }
    const months = Math.floor(diffDays / 30);
    return months === 1 ? "בעוד חודש" : `בעוד ${months} חודשים`;
}

function CalTooltipContent({events}) {
    const navigate = useNavigate();
    return (
        <TooltipBody>
            {events.map((ev, i) => {
                let icon, label, ageLabel;
                const fn = ev.type === EVENT_TYPE.ANNIVERSARY ? ev.firstName : `${ev.firstName} ${ev.lastName}`;
                if (ev.type === EVENT_TYPE.BIRTHDAY) {
                    icon = "🎂"; label = `יום הולדת ל${fn}`; ageLabel = ev.age ? `(${ev.age})` : "";
                } else if (ev.type === EVENT_TYPE.ANNIVERSARY) {
                    icon = "💍"; label = `יום הנישואין - ${fn}`; ageLabel = ev.age ? `(${ev.age} שנים)` : "";
                } else {
                    icon = "🕯️"; label = `יום הזכרון ל${fn}`; ageLabel = ev.age ? `(${ev.age} שנים)` : "";
                }
                return (
                    <TooltipEvent key={i} onClick={() => ev.memberId && navigate(`/?member=${ev.memberId}`)}>
                        {ev.avatar
                            ? <TooltipAvatar src={ev.avatar} alt={ev.name}/>
                            : <TooltipInitialsAvatar $color={getAvatarColor(ev.name)}>{getInitials(ev.firstName, ev.lastName)}</TooltipInitialsAvatar>}
                        <TooltipInfo>
                            <TooltipLabel>{icon} {label} {ageLabel}</TooltipLabel>
                            <TooltipDate>{formatDateHe(ev.day, ev.month)}</TooltipDate>
                        </TooltipInfo>
                    </TooltipEvent>
                );
            })}
        </TooltipBody>
    );
}

function MiniCalendar({events, viewMonth, onMonthChange}) {
    const [activeDay, setActiveDay] = useState(null);
    const today = new Date();
    const viewDate = viewMonth != null
        ? new Date(today.getFullYear(), viewMonth - 1, 1)
        : new Date(today.getFullYear(), today.getMonth(), 1);
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDow = firstDay.getDay();
    const daysInMonth = lastDay.getDate();
    const isCurrentMonth = month === today.getMonth() && year === today.getFullYear();

    const eventMap = {};
    for (const ev of events) {
        if (ev.month === month + 1) {
            const d = ev.day;
            if (!eventMap[d]) eventMap[d] = [];
            eventMap[d].push(ev);
        }
    }

    const cells = [];
    for (let i = 0; i < startDow; i++) cells.push(<CalCell key={`e-${i}`} />);
    for (let d = 1; d <= daysInMonth; d++) {
        const isToday = isCurrentMonth && d === today.getDate();
        const dayEvents = eventMap[d];
        const hasBirthday = dayEvents?.some(e => e.type === EVENT_TYPE.BIRTHDAY);
        const hasMemorial = dayEvents?.some(e => e.type === EVENT_TYPE.MEMORIAL);
        const hasAnniversary = dayEvents?.some(e => e.type === EVENT_TYPE.ANNIVERSARY);
        const hasEvents = !!(hasBirthday || hasMemorial || hasAnniversary);
        cells.push(
            <CalCellWrap key={d}>
                <CalCell $isToday={isToday} $hasBirthday={hasBirthday} $hasMemorial={hasMemorial} $hasAnniversary={hasAnniversary}
                         onClick={() => hasEvents && setActiveDay(activeDay === d ? null : d)}>
                    {d}
                    {hasBirthday && <CalDot>🎂</CalDot>}
                    {hasMemorial && <CalDot>🕯️</CalDot>}
                    {hasAnniversary && <CalDot>💍</CalDot>}
                </CalCell>
                {activeDay === d && dayEvents && (
                    <Tooltip>
                        <CalTooltipContent events={dayEvents}/>
                    </Tooltip>
                )}
            </CalCellWrap>
        );
    }

    const goPrev = () => { const m = month === 0 ? 12 : month; onMonthChange && onMonthChange(m); setActiveDay(null); };
    const goNext = () => { const m = month + 2 > 12 ? 1 : month + 2; onMonthChange && onMonthChange(m); setActiveDay(null); };
    const goToday = () => { onMonthChange && onMonthChange(null); setActiveDay(null); };

    return (
        <CalendarWrapper onClick={e => { if (e.target === e.currentTarget) setActiveDay(null); }}>
            <CalTitleRow>
                <CalNav data-glass onClick={goPrev}>&#8249;</CalNav>
                <CalTitle onClick={!isCurrentMonth ? goToday : undefined} style={!isCurrentMonth ? {cursor:'pointer', textDecoration:'underline dotted'} : {}}>
                    {MONTH_NAMES_HE[month]} {year}
                </CalTitle>
                <CalNav data-glass onClick={goNext}>&#8250;</CalNav>
            </CalTitleRow>
            <CalGrid>
                {["א׳", "ב׳", "ג׳", "ד׳", "ה׳", "ו׳", "ש׳"].map(day => (
                    <CalHeader key={day}>{day}</CalHeader>
                ))}
                {cells}
            </CalGrid>
        </CalendarWrapper>
    );
}

function EventCard({event}) {
    const navigate = useNavigate();
    const calUrl = buildGoogleCalendarUrl(event);
    const {type} = event;
    let icon, label, ageLabel;

    const fullName = event.type === EVENT_TYPE.ANNIVERSARY ? event.firstName : `${event.firstName} ${event.lastName}`;

    if (type === EVENT_TYPE.BIRTHDAY) {
        icon = "🎂";
        label = `יום הולדת ל${fullName}`;
        ageLabel = event.age ? `(${event.age})` : "";
    } else if (type === EVENT_TYPE.ANNIVERSARY) {
        icon = "💍";
        label = `יום הנישואין - ${fullName}`;
        ageLabel = event.age ? `(${event.age} שנים)` : "";
    } else {
        icon = "🕯️";
        label = `יום הזכרון ל${fullName}`;
        ageLabel = event.age ? `(${event.age} שנים)` : "";
    }

    const dateStr = formatDateHe(event.day, event.month);

    const goToTree = () => {
        if (event.memberId) navigate(`/?member=${event.memberId}`);
    };

    return (
        <Card $isToday={event.diffDays === 0} $isMemorial={type === EVENT_TYPE.MEMORIAL} $isAnniversary={type === EVENT_TYPE.ANNIVERSARY}>
            <AvatarWrap onClick={goToTree}>
                {event.avatar
                    ? <Avatar src={event.avatar} alt={event.name}/>
                    : <InitialsAvatar $color={getAvatarColor(event.name)}>{getInitials(event.firstName, event.lastName)}</InitialsAvatar>}
            </AvatarWrap>
            <CardBody onClick={goToTree} style={{cursor: "pointer"}}>
                <CardName>
                    {icon} {label}
                    {ageLabel && <CardAge>{ageLabel}</CardAge>}
                </CardName>
                <CardDate>{dateStr}</CardDate>
                <CardMeta>{formatRelativeDay(event.diffDays)}</CardMeta>
            </CardBody>
            <CalendarLink data-glass href={calUrl} target="_blank" rel="noopener noreferrer">
                ➕ הוסף ליומן
            </CalendarLink>
        </Card>
    );
}

export const Events = () => {
    const allEvents = useMemo(() => getAllEvents(), []);
    const [searchName, setSearchName] = useState("");
    const [filterType, setFilterType] = useState("all");
    const [filterMonth, setFilterMonth] = useState("all");

    const filtered = useMemo(() => {
        return allEvents.filter(ev => {
            if (searchName && !ev.name.includes(searchName) && !ev.firstName.includes(searchName)) return false;
            if (filterType !== "all" && ev.type !== filterType) return false;
            if (filterMonth !== "all" && ev.month !== parseInt(filterMonth, 10)) return false;
            return true;
        });
    }, [allEvents, searchName, filterType, filterMonth]);

    const hasFilters = searchName || filterType !== "all" || filterMonth !== "all";

    const todayEvents = filtered.filter(e => e.diffDays === 0);
    const tomorrowEvents = filtered.filter(e => e.diffDays === 1);

    const currentMonth = new Date().getMonth();
    const thisMonthEvents = filtered.filter(e => e.diffDays > 1 && e.date.getMonth() === currentMonth);
    const nextEvents = filtered.filter(e => e.diffDays > 1).slice(0, 8);

    const clearFilters = useCallback(() => {
        setSearchName("");
        setFilterType("all");
        setFilterMonth("all");
    }, []);

    return (
        <div className="page">
            <EventsBox>
                <PageTitle>אירועי המשפחה</PageTitle>

                <FiltersBar>
                    <SearchInput
                        type="text"
                        placeholder="🔍 חיפוש לפי שם..."
                        value={searchName}
                        onChange={e => setSearchName(e.target.value)}
                    />
                    <FilterGroup>
                        <FilterLabel>סוג:</FilterLabel>
                        <FilterSelect value={filterType} onChange={e => setFilterType(e.target.value)}>
                            <option value="all">הכל</option>
                            <option value={EVENT_TYPE.BIRTHDAY}>🎂 יום הולדת</option>
                            <option value={EVENT_TYPE.MEMORIAL}>🕯️ אזכרה</option>
                            <option value={EVENT_TYPE.ANNIVERSARY}>💍 יום הנישואין</option>
                        </FilterSelect>
                    </FilterGroup>
                    <FilterGroup>
                        <FilterLabel>חודש:</FilterLabel>
                        <FilterSelect value={filterMonth} onChange={e => setFilterMonth(e.target.value)}>
                            <option value="all">כל החודשים</option>
                            {MONTH_NAMES_HE.map((name, i) => (
                                <option key={i} value={i + 1}>{name}</option>
                            ))}
                        </FilterSelect>
                    </FilterGroup>
                    {hasFilters && <ClearBtn data-glass onClick={clearFilters}>✕ נקה</ClearBtn>}
                </FiltersBar>

                <MiniCalendar events={allEvents} viewMonth={filterMonth !== "all" ? parseInt(filterMonth, 10) : null} onMonthChange={m => setFilterMonth(m ? String(m) : "all")}/>

                {hasFilters ? (
                    <Section>
                        <SectionTitle>🔎 תוצאות ({filtered.length})</SectionTitle>
                        {filtered.length === 0
                            ? <EmptyState>לא נמצאו ארועים</EmptyState>
                            : filtered.slice(0, 20).map((ev, i) => <EventCard key={i} event={ev}/>)}
                    </Section>
                ) : (
                    <>
                        {todayEvents.length > 0 && (
                            <Section>
                                <SectionTitle>🎉 היום</SectionTitle>
                                {todayEvents.map((ev, i) => <EventCard key={i} event={ev}/>)}
                            </Section>
                        )}

                        {tomorrowEvents.length > 0 && (
                            <Section>
                                <SectionTitle>🎂 מחר</SectionTitle>
                                {tomorrowEvents.map((ev, i) => <EventCard key={i} event={ev}/>)}
                            </Section>
                        )}

                        {thisMonthEvents.length > 0 ? (
                            <Section>
                                <SectionTitle>📅 החודש</SectionTitle>
                                {thisMonthEvents.map((ev, i) => <EventCard key={i} event={ev}/>)}
                            </Section>
                        ) : (
                            <Section>
                                <SectionTitle>📅 הארועים הקרובים</SectionTitle>
                                {nextEvents.length === 0
                                    ? <EmptyState>אין ארועים קרובים</EmptyState>
                                    : nextEvents.map((ev, i) => <EventCard key={i} event={ev}/>)}
                            </Section>
                        )}
                    </>
                )}
                <ContactFooter/>
            </EventsBox>
        </div>
    );
};

const fadeIn = keyframes`
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
`;

const EventsBox = styled.div`
    width: 100%;
    max-width: 800px;
    height: max-content;
    padding: 8px 16px 96px;
    box-sizing: border-box;
    direction: rtl;
    text-align: right;
    animation: ${fadeIn} 0.4s ease-out;

    @media (min-width: 768px) {
        padding: 24px 32px 72px;
    }
`;

/* iOS large title */
const PageTitle = styled.h1`
    font-size: clamp(32px, 5vw, 44px);
    font-weight: 800;
    line-height: 1.08;
    letter-spacing: -0.025em;
    margin: 12px 4px 20px;
    color: var(--label);
`;

const FiltersBar = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
    margin-bottom: 16px;
    padding: 12px;
    background: var(--surface);
    border-radius: var(--radius-md);
    box-shadow: var(--shadow-sm);
`;

const SearchInput = styled.input`
    flex: 1;
    min-width: 160px;
    padding: 9px 14px;
    border: none;
    border-radius: 10px;
    background: rgba(118, 118, 128, 0.12);
    font-size: 17px;
    direction: rtl;
    outline: none;
    transition: box-shadow 0.2s;
    font-family: inherit;
    color: var(--label);

    &:focus {
        box-shadow: 0 0 0 3px rgba(0, 122, 255, 0.25);
    }

    &::placeholder {
        color: var(--label-3);
    }
`;

const FilterGroup = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
`;

const FilterLabel = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: #636e72;
    white-space: nowrap;
`;

const FilterSelect = styled.select`
    padding: 8px 12px;
    border: none;
    border-radius: 10px;
    font-size: 15px;
    background: rgba(118, 118, 128, 0.12);
    color: var(--label);
    cursor: pointer;
    outline: none;
    font-family: inherit;
    direction: rtl;

    &:focus {
        box-shadow: 0 0 0 3px rgba(0, 122, 255, 0.25);
    }
`;

const ClearBtn = styled.button`
    ${capsule}
    ${glass}
    min-height: 36px;
    padding: 0 16px;
    font-size: 15px;
    color: #ff3b30;
`;

const Section = styled.div`
    margin-bottom: 24px;
`;

const SectionTitle = styled.h2`
    font-size: 22px;
    font-weight: 700;
    letter-spacing: -0.02em;
    color: var(--label);
    margin: 0 4px 12px;
`;

const EmptyState = styled.div`
    color: #95a5a6;
    font-size: 15px;
    padding: 8px 0;
`;

const cardPop = keyframes`
    from { opacity: 0; transform: scale(0.95); }
    to { opacity: 1; transform: scale(1); }
`;

const Card = styled.div`
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 14px 18px;
    border-radius: 12px;
    margin-bottom: 10px;
    animation: ${cardPop} 0.4s ease-out;
    background: ${p => {
        if (p.$isToday && p.$isMemorial) return "linear-gradient(135deg, #dfe6e9 0%, #b2bec3 100%)";
        if (p.$isToday && p.$isAnniversary) return "linear-gradient(135deg, #fce4ec 0%, #f8bbd0 100%)";
        if (p.$isToday) return "linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)";
        if (p.$isAnniversary) return "#fce4ec";
        if (p.$isMemorial) return "#f5f6fa";
        return "var(--surface)";
    }};
    box-shadow: var(--shadow-sm);
    border: ${p => {
        if (p.$isToday) {
            if (p.$isMemorial) return "2px solid #636e72";
            if (p.$isAnniversary) return "2px solid #e91e63";
            return "2px solid #e17055";
        }
        if (p.$isAnniversary) return "1px solid #f48fb1";
        return "1px solid transparent";
    }};
    border-radius: var(--radius-md);
    transition: transform 420ms var(--ease), box-shadow 420ms var(--ease);

    @media (hover: hover) and (pointer: fine) {
        &:hover {
            transform: translateY(-2px);
            box-shadow: var(--shadow-lg);
        }
    }
`;

const AvatarWrap = styled.div`
    cursor: pointer;
`;

const Avatar = styled.img`
    width: 48px;
    height: 48px;
    border-radius: 50%;
    object-fit: cover;
    border: 2px solid #dfe6e9;
`;

const InitialsAvatar = styled.div`
    width: 48px;
    height: 48px;
    border-radius: 50%;
    border: 2px solid #dfe6e9;
    background: ${p => p.$color};
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 17px;
    font-weight: 600;
    user-select: none;
`;

const CardBody = styled.div`
    flex: 1;
`;

const CardName = styled.div`
    font-size: 17px;
    font-weight: 600;
    color: #2d3436;
`;

const CardAge = styled.span`
    font-weight: 400;
    color: #636e72;
    margin-right: 6px;
`;

const CardDate = styled.div`
    font-size: 13px;
    color: #636e72;
    margin-top: 2px;
`;

const CardMeta = styled.div`
    font-size: 13px;
    color: #74b9ff;
    font-weight: 500;
    margin-top: 2px;
`;

const CalendarLink = styled.a`
    ${capsule}
    ${filled}
    min-height: 34px;
    padding: 0 14px;
    font-size: 13px;
`;

const CalendarWrapper = styled.div`
    background: var(--surface);
    border-radius: var(--radius-md);
    box-shadow: var(--shadow-sm);
    padding: 16px;
    margin-bottom: 24px;
`;

const CalTitleRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    margin-bottom: 12px;
`;

const CalNav = styled.button`
    ${glass}
    border: none;
    border-radius: 50%;
    width: 38px;
    height: 38px;
    font-size: 20px;
    color: var(--tint);
    display: flex;
    align-items: center;
    justify-content: center;
`;

const CalTitle = styled.div`
    text-align: center;
    font-size: 17px;
    font-weight: 600;
    color: #2c3e50;
`;

const CalGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 4px;
    direction: ltr;
`;

const CalHeader = styled.div`
    text-align: center;
    font-size: 12px;
    font-weight: 600;
    color: #95a5a6;
    padding: 4px 0;
`;

const CalCellWrap = styled.div`
    position: relative;
`;

const CalCell = styled.div`
    text-align: center;
    font-size: 14px;
    padding: 6px 2px;
    border-radius: 8px;
    min-height: 32px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: ${p => p.$isToday ? "white" : "#2d3436"};
    background: ${p => {
        if (p.$isToday) return "#0984e3";
        if (p.$hasBirthday) return "#ffeaa7";
        if (p.$hasAnniversary) return "#fce4ec";
        if (p.$hasMemorial) return "#dfe6e9";
        return "transparent";
    }};
    font-weight: ${p => (p.$isToday || p.$hasBirthday || p.$hasMemorial || p.$hasAnniversary) ? "700" : "400"};
    cursor: ${p => (p.$hasBirthday || p.$hasMemorial || p.$hasAnniversary) ? "pointer" : "default"};
    transition: transform 0.15s;

    &:hover {
        transform: ${p => (p.$hasBirthday || p.$hasMemorial || p.$hasAnniversary) ? "scale(1.15)" : "none"};
    }
`;

const CalDot = styled.span`
    font-size: 10px;
    line-height: 1;
`;

const Tooltip = styled.div`
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    z-index: 100;
    min-width: 220px;
    max-width: 280px;
    background: white;
    border-radius: 12px;
    box-shadow: 0 8px 24px rgba(0,0,0,0.18);
    border: 1px solid #e9ecef;
    padding: 10px;
    margin-top: 6px;
    direction: rtl;

    &::before {
        content: "";
        position: absolute;
        top: -6px;
        left: 50%;
        transform: translateX(-50%) rotate(45deg);
        width: 12px;
        height: 12px;
        background: white;
        border-top: 1px solid #e9ecef;
        border-left: 1px solid #e9ecef;
    }
`;

const TooltipBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const TooltipEvent = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px;
    border-radius: 8px;
    cursor: pointer;
    transition: background 0.15s;

    &:hover {
        background: #f1f3f5;
    }
`;

const TooltipAvatar = styled.img`
    width: 36px;
    height: 36px;
    border-radius: 50%;
    object-fit: cover;
    border: 2px solid #dfe6e9;
    flex-shrink: 0;
`;

const TooltipInitialsAvatar = styled.div`
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border: 2px solid #dfe6e9;
    background: ${p => p.$color};
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    font-weight: 600;
    flex-shrink: 0;
    user-select: none;
`;

const TooltipInfo = styled.div`
    flex: 1;
    min-width: 0;
`;

const TooltipLabel = styled.div`
    font-size: 14px;
    font-weight: 600;
    color: #2d3436;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const TooltipDate = styled.div`
    font-size: 12px;
    color: #74b9ff;
    font-weight: 500;
    margin-top: 1px;
`;
